import { Injectable, inject, signal } from '@angular/core';
import { Observable, from, map, catchError, of, switchMap } from 'rxjs';
import { PocketbaseService } from './pocketbase.service';

export interface DiscussionLike {
  id: string;
  discussion: string;
  user: string;
  created: string;
}

@Injectable({
  providedIn: 'root'
})
export class DiscussionLikesService {
  private readonly pocketbaseService = inject(PocketbaseService);
  
  // Track which discussions the current user has liked (in-memory cache)
  private readonly userLikes = signal<Set<string>>(new Set());

  /**
   * Load all likes for the current user for given discussion IDs
   */
  loadUserLikes(discussionIds: string[]): Observable<Set<string>> {
    const userId = this.pocketbaseService.getPocketBase().authStore.model?.['id'];
    if (!userId || discussionIds.length === 0) {
      return of(new Set<string>());
    }

    // Build filter: user = "xyz" && (discussion = "id1" || discussion = "id2" || ...)
    const discussionFilters = discussionIds.map(id => `discussion = "${id}"`).join(' || ');
    const filter = `user = "${userId}" && (${discussionFilters})`;

    return from(
      this.pocketbaseService.getPocketBase()
        .collection('discussion_likes')
        .getFullList<DiscussionLike>({ filter })
    ).pipe(
      map(likes => {
        const likedDiscussions = new Set<string>(likes.map(like => like.discussion));
        this.userLikes.set(likedDiscussions);
        return likedDiscussions;
      }),
      catchError(error => {
        console.error('Failed to load user likes:', error);
        return of(new Set<string>());
      })
    );
  }

  /**
   * Check if current user has liked a discussion (from cache)
   */
  isLiked(discussionId: string): boolean {
    return this.userLikes().has(discussionId);
  }

  /**
   * Toggle like: create if not liked, delete if already liked
   */
  toggleLike(discussionId: string): Observable<{ liked: boolean; likeId?: string }> {
    const userId = this.pocketbaseService.getPocketBase().authStore.model?.['id'];
    if (!userId) {
      throw new Error('User must be authenticated to like');
    }

    const isCurrentlyLiked = this.isLiked(discussionId);

    if (isCurrentlyLiked) {
      // Unlike: Find and delete the like
      return this.findLike(discussionId, userId).pipe(
        switchMap((likeId: string | null) => {
          if (!likeId) {
            // Like not found, just update local state
            this.userLikes.update(likes => {
              const newLikes = new Set(likes);
              newLikes.delete(discussionId);
              return newLikes;
            });
            return of({ liked: false });
          }

          return from(
            this.pocketbaseService.getPocketBase()
              .collection('discussion_likes')
              .delete(likeId)
          ).pipe(
            map(() => {
              // Update local cache
              this.userLikes.update(likes => {
                const newLikes = new Set(likes);
                newLikes.delete(discussionId);
                return newLikes;
              });
              return { liked: false };
            }),
            catchError(() => of({ liked: true })) // Keep as liked on error
          );
        })
      );
    } else {
      // Like: Create new like
      return from(
        this.pocketbaseService.getPocketBase()
          .collection('discussion_likes')
          .create<DiscussionLike>({
            discussion: discussionId,
            user: userId
          })
      ).pipe(
        map(like => {
          // Update local cache
          this.userLikes.update(likes => {
            const newLikes = new Set(likes);
            newLikes.add(discussionId);
            return newLikes;
          });
          return { liked: true, likeId: like.id };
        }),
        catchError(() => of({ liked: false })) // Keep as not liked on error
      );
    }
  }

  /**
   * Find a like record by discussion and user
   */
  private findLike(discussionId: string, userId: string): Observable<string | null> {
    const filter = `discussion = "${discussionId}" && user = "${userId}"`;
    
    return from(
      this.pocketbaseService.getPocketBase()
        .collection('discussion_likes')
        .getFirstListItem<DiscussionLike>(filter)
    ).pipe(
      map(like => like.id),
      catchError(() => of(null))
    );
  }

  /**
   * Clear the cache (e.g., on logout)
   */
  clearCache(): void {
    this.userLikes.set(new Set());
  }
}

